// The jet-suit in VR (owner 2026-11-03, supersedes 2026-11-02's split of a
// left-stick pitch + a right-stick turn; APP_PLAYER "VR controls" → jet-suit):
// the VR INPUT MAPPING onto the one flight model, pure (no THREE, no XR).
//
// Owner, 2026-11-03: "press right: jump, i.e. start jetsuit. [...] forward =
// tilt down, backward = up, right/left is steer. [...] don't bring up the zoom
// map, flying is to look around. the speed is automatic, so after jumping,
// slight acceleration, that after the first 3 seconds or so, when it knows the
// intention was to fly goes sharper up, then physically accurate flying." —
// corrected the same day: "click right stick → jump; then the steering and
// tilting with the LEFT stick!" The right stick does nothing in flight.
//
// The flight model is Player._stepJet (player.js) on every device: it chases
// steerDir × vmax (climb capped, a constant sink, accel-limited), so climbing
// bleeds speed and diving gains it. Desktop feeds it the camera forward (mouse
// yaw + pitch); VR feeds it the SAME vector built from a FLIGHT yaw and a
// FLIGHT pitch the LEFT stick owns — the head only looks around:
//   • heading: left stick X turns it, ALWAYS smooth (glide.stick_turn_rate_deg
//     90°/s at full, whatever ☰ → Comfort says — walking keeps the snap);
//     main.js turns the rig by the same dYaw, so the view turns with the body.
//     Seeded at the ignition from the body's horizontal velocity (where the
//     jump was going), else from the caller's seed yaw (main.js: the GAZE
//     heading — a standing jump flies where you look).
//   • pitch: left stick Y is a pitch TARGET (pulled back = nose up, pushed =
//     nose down, × glide.vr_pitch_max_deg), slewed at glide.stick_pitch_rate_deg;
//     released = the launch's pitch, after the launch level. |pitch| < 90°, so
//     the steer always points forward: no reverse, no brake (the flare is
//     desktop's S only). X and Y act together (no axis lock in flight).
//   • the right stick: nothing (main.js: no comfort turn, no zoom window, no
//     walk; the left stick's Map click is off in flight too).
//   • THE LAUNCH (the speed is automatic): flight time t from the ignition —
//     t < vr_launch_gentle_s (3 s): thrust × vr_launch_thrust (gentle) at an
//     auto pitch of vr_launch_pitch_deg (a slight rise); then the thrust ramps
//     to full over vr_launch_ramp_s and the auto pitch is
//     vr_launch_climb_pitch_deg (sharper up) until gentle + vr_launch_climb_s;
//     then level, full thrust: the plain flight model. A pushed / pulled stick
//     always wins over the auto pitch. `thrust` → main.js extras.jetThrust →
//     _stepJet (a scale on its accel; desktop passes none = 1).
// The sticks arrive through the STICK GATE (vrstick.js): an untouched stick
// reads 0, so a drifting stick neither steers nor pitches.
// dir is the camera forward of (yaw, pitch) — the same formula three's
// getWorldDirection yields for rotation (pitch, yaw, 0, 'YXZ').

const D2R = Math.PI / 180;
export const JET_VR_DEAD = 0.15;      // the flight stick's dead zone (both axes)

// a dead-zoned, rescaled axis: 0 inside JET_VR_DEAD, ±1 at the rim
export function jetAxis(v) {
  const a = Math.abs(v);
  return a > JET_VR_DEAD ? Math.sign(v) * Math.min(1, (a - JET_VR_DEAD) / (1 - JET_VR_DEAD)) : 0;
}

// the launch profile at flight time t (s): { thrust, pitch (deg) } — pure
export function jetLaunch(g, t) {
  const t1 = g.vr_launch_gentle_s ?? 3, ramp = Math.max(1e-3, g.vr_launch_ramp_s ?? 1);
  const t2 = t1 + (g.vr_launch_climb_s ?? 3), lt = g.vr_launch_thrust ?? 0.25;
  let thrust = 1;
  if (t < t1) thrust = lt;
  else if (t < t1 + ramp) { const u = (t - t1) / ramp; thrust = lt + (1 - lt) * u * u * (3 - 2 * u); }
  const pitch = t < t1 ? (g.vr_launch_pitch_deg ?? 3) : t < t2 ? (g.vr_launch_climb_pitch_deg ?? 25) : 0;
  return { thrust, pitch };
}

export class JetVr {
  constructor() {
    this.yaw = 0;          // the flight heading (rig convention: forward = (−sin, 0, −cos))
    this.pitch = 0;        // rad, + = nose up
    this.dYaw = 0;         // this frame's turn — main.js turns the rig by it
    this.t = 0;            // s since the ignition (the launch profile's clock)
    this.thrust = 1;       // the launch's thrust scale → extras.jetThrust
    this.dir = { x: 0, y: 0, z: -1 };
  }

  // tun: the tunables (read each frame: a Save swaps them); gliding:
  // player.gliding; vel: the body's velocity; seedYaw: the heading when the
  // body stands still at the ignition (main.js: the gaze heading); inp:
  // VRControls.poll's out (axL — the gated left stick; nothing else is read).
  update(dt, tun, gliding, vel, seedYaw, inp) {
    const g = (tun && tun.glide) || {};
    this.dYaw = 0;
    if (!gliding) {
      // not flying: the ignition's steer is the level line of the jump
      const h = Math.hypot(vel.x, vel.z);
      this.yaw = h > 0.1 ? Math.atan2(-vel.x, -vel.z) : seedYaw;
      this.pitch = 0;
      this.t = 0;
      this.thrust = jetLaunch(g, 0).thrust;   // the ignition frame flies gently already
    } else {
      const L = jetLaunch(g, this.t);
      this.thrust = L.thrust;
      this.t += dt;
      const sx = jetAxis(inp.axL.x), sy = jetAxis(inp.axL.y);
      // ALWAYS smooth (owner 2026-11-03): the ☰ Comfort snap step never applies in flight
      if (sx) this.dYaw = -sx * (g.stick_turn_rate_deg ?? 90) * D2R * dt;
      this.yaw += this.dYaw;
      const pmax = Math.min(85, g.vr_pitch_max_deg ?? 60) * D2R;
      // sy > 0 = pulled back = nose up; released = the launch's pitch (then level)
      const target = sy ? sy * pmax : Math.max(-pmax, Math.min(pmax, L.pitch * D2R));
      const step = (g.stick_pitch_rate_deg ?? 60) * D2R * dt;
      this.pitch += Math.max(-step, Math.min(step, target - this.pitch));
    }
    const cp = Math.cos(this.pitch);
    this.dir.x = -Math.sin(this.yaw) * cp;
    this.dir.y = Math.sin(this.pitch);
    this.dir.z = -Math.cos(this.yaw) * cp;
    return this.dir;
  }
}
